import {
  NexusUpdateDto,
  NodoDto,
  getGetNodoPortaleQueryKey,
  getGetNodoQueryKey,
  useUpdateNexus,
} from "@/api";
import Box from "@/elements/Box";
import Button from "@/elements/Button";
import Input from "@/elements/Input";
import Typography from "@/elements/Typography";
import { withOpeningReset } from "@/utils/withOpeningReset";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { toast } from "react-toastify";
import { NodoPicker } from "../NodoPicker";
import { portaleConfig } from "@/portaliConfig";

export type RegistriNexusDialogProps = {
  open?: boolean;
  onClose?: () => void;
  dispositivo?: NodoDto;
};

const PIN_OPTIONS = [
  { value: "pin_1", numero: 1 },
  { value: "pin_2", numero: 2 },
  { value: "pin_3", numero: 3 },
  { value: "pin_4", numero: 4 },
  { value: "pin_5", numero: 5 },
  { value: "pin_6", numero: 6 },
];

function RegistriNexusDialog({
  open = false,
  onClose,
  dispositivo,
}: RegistriNexusDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const {
    control,
    handleSubmit,
    formState: { isDirty, errors },
    reset,
    register,
    setError,
    setValue,
  } = useForm<NexusUpdateDto>({
    defaultValues: {
      attuatori: dispositivo?.dispositivoNexusAttuatori ?? [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "attuatori",
  });

  const { mutateAsync, isLoading } = useUpdateNexus();

  useEffect(() => {
    if (open && dispositivo?.dispositivoNexusAttuatori) {
      reset({
        attuatori: dispositivo.dispositivoNexusAttuatori ?? [],
      });
    }
  }, [open, dispositivo, reset]);

  const handleClose = () => {
    onClose?.();
    reset();
  };

  async function onSubmit(data: NexusUpdateDto) {
    if (!dispositivo?.id) {
      return;
    }
    try {
      await mutateAsync({
        id: dispositivo.id,
        data,
      });
      await queryClient.invalidateQueries(
        getGetNodoPortaleQueryKey(portaleConfig.id, dispositivo.id)
      );
      await queryClient.invalidateQueries(getGetNodoQueryKey(dispositivo.id));
      toast.success(t("registriNexus.toast.success"));
      handleClose();
    } catch (e: any) {
      console.error(e);
      const serverErrors = e.response?.data?.errors;
      if (serverErrors) {
        Object.entries(serverErrors).forEach(([key, messages]) => {
          setError(key as any, {
            type: "manual",
            message: (messages as string[]).join(", "),
          });
        });
      } else {
        toast.error(t("registriNexus.toast.error"));
      }
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xl">
      <DialogTitle>
        {t("registriNexus.titolo", { nome: dispositivo?.nome ?? "" })}
      </DialogTitle>
      <DialogContent>
        <Box mt={2}>
          {fields.map((field, index) => (
            <ActuatorCard
              key={field.id}
              index={index}
              control={control}
              register={register}
              remove={() => remove(index)}
              errors={errors}
              disabled={isLoading}
              setValue={setValue}
            />
          ))}

          <Box display="flex" justifyContent="center">
            <Box>
              <Button
                variant="outlined"
                color="info"
                startIcon={<AddIcon />}
                disabled={isLoading}
                onClick={() =>
                  append({
                    nome: "",
                    statoAnalisiPin: "",
                    riduzionePin: "",
                    riduzioneMappatura: {},
                    sensori: [],
                  })
                }
              >
                {t("registriNexus.azioni.aggiungiAttuatore")}
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          disabled={isLoading || !isDirty}
          onClick={handleSubmit(onSubmit)}
        >
          {t("azioni.salva")}
        </Button>
        <Button
          variant="contained"
          color="light"
          disabled={isLoading}
          onClick={handleClose}
        >
          {t("azioni.annulla")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ActuatorCard({
  index,
  control,
  errors,
  register,
  remove,
  disabled,
  setValue,
}: {
  index: number;
  control: Control<NexusUpdateDto>;
  errors: FieldErrors<NexusUpdateDto>;
  register: UseFormRegister<NexusUpdateDto>;
  remove: () => void;
  disabled?: boolean;
  setValue: UseFormSetValue<NexusUpdateDto>;
}) {
  const { t } = useTranslation();
  const {
    fields: sensori,
    append,
    remove: removeSensore,
  } = useFieldArray({
    control,
    name: `attuatori.${index}.sensori`,
  });

  return (
    <Box bgColor="grey-100" borderRadius="lg" p={3} mb={3} position="relative">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6" fontWeight="bold">
          {t("registriNexus.attuatore", { numero: index + 1 })}
        </Typography>
        <IconButton
          onClick={remove}
          color="error"
          size="small"
          disabled={disabled}
        >
          <DeleteIcon />
        </IconButton>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Box mb={1}>
            <Typography
              variant="caption"
              fontWeight="bold"
              textTransform="uppercase"
            >
              {t("registriNexus.campi.nomeAttuatore")}
            </Typography>
          </Box>
          <Input
            {...register(`attuatori.${index}.nome`, {
              required: t("validazione.campoObbligatorio"),
            })}
            placeholder={t("registriNexus.placeholder.nomeAttuatore")}
            fullWidth
            disabled={disabled}
          />
          {errors.attuatori?.[index]?.nome?.message ? (
            <FormHelperText error>
              {errors.attuatori[index]!.nome!.message}
            </FormHelperText>
          ) : null}
        </Grid>
        <Grid item xs={12} md={4}>
          <Box mb={1}>
            <Typography
              variant="caption"
              fontWeight="bold"
              textTransform="uppercase"
            >
              {t("registriNexus.campi.inputStatoAnalisi")}
            </Typography>
          </Box>
          <FormControl fullWidth disabled={disabled}>
            <Controller
              control={control}
              name={`attuatori.${index}.statoAnalisiPin`}
              render={({ field }) => {
                return (
                  <Select
                    value={field.value}
                    onChange={field.onChange}
                    size="small"
                    sx={{ height: "40px", backgroundColor: "white" }}
                  >
                    <MenuItem value="">
                      {t("registriNexus.opzioni.nessuno")}
                    </MenuItem>
                    {PIN_OPTIONS.map((pin) => (
                      <MenuItem key={pin.value} value={pin.value}>
                        {t("registriNexus.opzioni.pin", {
                          numero: pin.numero,
                        })}
                      </MenuItem>
                    ))}
                  </Select>
                );
              }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box mb={1}>
            <Typography
              variant="caption"
              fontWeight="bold"
              textTransform="uppercase"
            >
              {t("registriNexus.campi.inputRiduzione")}
            </Typography>
          </Box>
          <FormControl fullWidth disabled={disabled}>
            <Controller
              control={control}
              name={`attuatori.${index}.riduzionePin`}
              render={({ field }) => {
                return (
                  <Select
                    value={field.value}
                    onChange={field.onChange}
                    size="small"
                    sx={{ height: "40px", backgroundColor: "white" }}
                  >
                    <MenuItem value="">
                      {t("registriNexus.opzioni.nessuno")}
                    </MenuItem>
                    {PIN_OPTIONS.map((pin) => (
                      <MenuItem key={pin.value} value={pin.value}>
                        {t("registriNexus.opzioni.pin", {
                          numero: pin.numero,
                        })}
                      </MenuItem>
                    ))}
                  </Select>
                );
              }}
            />
          </FormControl>

          <ActuatorMappingTable
            index={index}
            control={control}
            setValue={setValue}
            disabled={disabled}
          />
        </Grid>

        <Grid item xs={12}>
          <Box mt={2} mb={1}>
            <Typography
              variant="button"
              fontWeight="bold"
              textTransform="uppercase"
            >
              {t("registriNexus.sensori.titolo")}
            </Typography>
          </Box>
          <TableContainer sx={{ backgroundColor: "white", borderRadius: "md" }}>
            <Table size="small">
              <Box component="thead">
                <TableRow>
                  <TableCell>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="text"
                    >
                      {t("registriNexus.sensori.colonne.sensore")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="text"
                    >
                      {t("registriNexus.sensori.colonne.registroAnalisi")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="text"
                    >
                      {t("registriNexus.sensori.colonne.registroRiduzione")}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="text"
                    >
                      {t("registriNexus.sensori.colonne.inibisciEmail")}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" width={50}>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="text"
                    ></Typography>
                  </TableCell>
                </TableRow>
              </Box>
              <TableBody>
                {sensori.map((sensore, sIndex) => (
                  <RigaSensore
                    key={sensore.id}
                    attuatoreIndex={index}
                    sensoreIndex={sIndex}
                    control={control}
                    register={register}
                    errors={errors}
                    remove={() => removeSensore(sIndex)}
                    disabled={disabled}
                  />
                ))}
                {sensori.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="caption" color="text">
                        {t("registriNexus.sensori.vuoto")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            variant="text"
            color="info"
            size="small"
            startIcon={<AddIcon />}
            disabled={disabled}
            onClick={() =>
              append({
                sensoreNodoId: undefined,
                statoAnalisiRegistroNome: "",
                riduzioneRegistroNome: "",
                statoAnalisiInibisceEmail: false,
              })
            }
            sx={{ mt: 1 }}
          >
            {t("registriNexus.azioni.aggiungiSensore")}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

function ActuatorMappingTable({
  index,
  control,
  setValue,
  disabled,
}: {
  index: number;
  control: Control<NexusUpdateDto>;
  setValue: UseFormSetValue<NexusUpdateDto>;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const riduzionePin = useWatch({
    control,
    name: `attuatori.${index}.riduzionePin`,
  });

  const riduzioneMappatura =
    useWatch({
      control,
      name: `attuatori.${index}.riduzioneMappatura`,
    }) || {};

  if (!riduzionePin) {
    return null;
  }

  const entries = Object.entries(riduzioneMappatura);

  const handleUpdateKey = (oldKey: string, newKey: string) => {
    if (oldKey === newKey) return;
    const value = riduzioneMappatura[oldKey];
    const newMappatura = { ...riduzioneMappatura };
    delete newMappatura[oldKey];
    newMappatura[newKey] = value;
    setValue(`attuatori.${index}.riduzioneMappatura`, newMappatura, {
      shouldDirty: true,
    });
  };

  const handleUpdateValue = (key: string, newValue: string) => {
    const newMappatura = { ...riduzioneMappatura, [key]: newValue };
    setValue(`attuatori.${index}.riduzioneMappatura`, newMappatura, {
      shouldDirty: true,
    });
  };

  const handleRemove = (key: string) => {
    const newMappatura = { ...riduzioneMappatura };
    delete newMappatura[key];
    setValue(`attuatori.${index}.riduzioneMappatura`, newMappatura, {
      shouldDirty: true,
    });
  };

  return (
    <Box mt={2}>
      <Box mb={1}>
        <Typography
          variant="button"
          fontWeight="bold"
          textTransform="uppercase"
        >
          {t("registriNexus.mappatura.titolo")}
        </Typography>
      </Box>

      <TableContainer sx={{ backgroundColor: "white", borderRadius: "md" }}>
        <Table size="small">
          <Box component="thead">
            <TableRow>
              <TableCell>
                <Typography variant="caption" fontWeight="bold" color="text">
                  {t("registriNexus.mappatura.colonne.valoreNexus")}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption" fontWeight="bold" color="text">
                  {t("registriNexus.mappatura.colonne.valoreSensore")}
                </Typography>
              </TableCell>
              <TableCell align="right" width={50}></TableCell>
            </TableRow>
          </Box>
          <TableBody>
            {entries.map(([key, value], idx) => (
              <TableRow key={idx}>
                <TableCell>
                  <Input
                    value={key}
                    onChange={(e: any) => handleUpdateKey(key, e.target.value)}
                    size="small"
                    disabled={disabled}
                    placeholder={t("registriNexus.mappatura.placeholder.valoreNexus")}
                  />
                </TableCell>
                <TableCell>
                  <Input
                    value={value}
                    onChange={(e: any) =>
                      handleUpdateValue(key, e.target.value)
                    }
                    size="small"
                    disabled={disabled}
                    placeholder={t("registriNexus.mappatura.placeholder.valoreRegistro")}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={() => handleRemove(key)}
                    color="error"
                    size="small"
                    disabled={disabled}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            <TableRow key={entries.length}>
              <TableCell>
                <Input
                  onBlur={(e: any) => {
                    if (e.target.value) {
                      handleUpdateValue(e.target.value, "");
                    }
                  }}
                  size="small"
                  disabled={disabled}
                  placeholder={t("registriNexus.mappatura.placeholder.valoreNexus")}
                />
              </TableCell>
              <TableCell>
                <Input
                  onBlur={(e: any) => {
                    if (e.target.value) {
                      handleUpdateValue("", e.target.value);
                    }
                  }}
                  size="small"
                  disabled={disabled}
                  placeholder={t("registriNexus.mappatura.placeholder.valoreRegistro")}
                />
              </TableCell>
              <TableCell align="right" width={50}></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

const nodoPickerSelection = (n: NodoDto) =>
  n.tipo === "Azienda" ||
  n.tipo === "Contenitore" ||
  n.tipo === "Dispositivo_Sensore";

function RigaSensore({
  attuatoreIndex,
  sensoreIndex,
  control,
  register,
  errors,
  remove,
  disabled,
}: {
  attuatoreIndex: number;
  sensoreIndex: number;
  control: Control<NexusUpdateDto>;
  register: UseFormRegister<NexusUpdateDto>;
  errors: FieldErrors<NexusUpdateDto>;
  remove: () => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <TableRow>
      <TableCell sx={{ minWidth: 250 }}>
        <Controller
          name={`attuatori.${attuatoreIndex}.sensori.${sensoreIndex}.sensoreNodoId`}
          control={control}
          rules={{
            required: t("validazione.campoObbligatorio"),
          }}
          render={({ field }) => (
            <NodoPicker
              grow="horizontally"
              value={field.value ? { id: field.value } : null}
              readOnly={disabled}
              onChange={(nodo) => {
                field.onChange(nodo?.id ?? null);
              }}
              filterSelection={nodoPickerSelection}
            />
          )}
        />
        {errors.attuatori?.[attuatoreIndex]?.sensori?.[sensoreIndex]
          ?.sensoreNodoId?.message ? (
          <FormHelperText error>
            {
              errors.attuatori[attuatoreIndex]!.sensori![sensoreIndex]!
                .sensoreNodoId!.message
            }
          </FormHelperText>
        ) : null}
      </TableCell>
      <TableCell>
        <Input
          {...register(
            `attuatori.${attuatoreIndex}.sensori.${sensoreIndex}.statoAnalisiRegistroNome`
          )}
          placeholder={t("registriNexus.placeholder.registroAnalisi")}
          size="small"
          disabled={disabled}
        />
      </TableCell>
      <TableCell>
        <Input
          {...register(
            `attuatori.${attuatoreIndex}.sensori.${sensoreIndex}.riduzioneRegistroNome`
          )}
          placeholder={t("registriNexus.placeholder.registroRiduzione")}
          size="small"
          disabled={disabled}
        />
      </TableCell>
      <TableCell align="center">
        <Controller
          name={`attuatori.${attuatoreIndex}.sensori.${sensoreIndex}.statoAnalisiInibisceEmail`}
          control={control}
          render={({ field }) => (
            <Checkbox
              {...field}
              checked={!!field.value}
              size="small"
              disabled={disabled}
            />
          )}
        />
      </TableCell>
      <TableCell align="right">
        <IconButton
          onClick={remove}
          color="error"
          size="small"
          disabled={disabled}
        >
          <DeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

export default withOpeningReset(RegistriNexusDialog);
